import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import {
  ArrowLeft,
  FileText,
  Database,
  Cloud,
  Package,
  CheckCircle2,
  ShieldCheck,
  Layers,
  RefreshCw,
} from "lucide-react";

export const Route = createFileRoute("/admin_/ownership-portability")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Platform Ownership & Portability — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: OwnershipPortabilityPage,
});

const LAST_REVIEWED = "16 June 2026";
const NEXT_REVIEW = "16 June 2027";
const DOCUMENT_OWNER = "Jana Burkhardt — Founder & Product Compliance Lead";

const OWNERSHIP_ITEMS = [
  "Source code",
  "Database content",
  "Brand assets",
  "Educational content",
  "Therapist profiles",
  "Platform documentation",
  "User-generated content subject to applicable Terms and Privacy Policies",
];

const TECH_STACK = [
  "React",
  "TypeScript",
  "Tailwind CSS",
  "TanStack Start",
  "PostgreSQL",
  "Supabase",
];

const DEPLOYMENT_TARGETS = [
  "Vercel",
  "Netlify",
  "AWS",
  "Azure",
  "DigitalOcean",
  "Cloudflare",
];

const CONTINUITY_MEASURES = [
  "Source code backups",
  "GitHub repository ownership",
  "Database export procedures",
  "Documentation of platform architecture",
  "Compliance documentation",
  "Governance documentation",
];

function exportCSV() {
  const rows: string[][] = [
    ["Section", "Item"],
    ...OWNERSHIP_ITEMS.map((i) => ["Ownership", i]),
    ...TECH_STACK.map((i) => ["Technology Stack", i]),
    ...DEPLOYMENT_TARGETS.map((i) => ["Deployment Environment", i]),
    ...CONTINUITY_MEASURES.map((i) => ["Business Continuity", i]),
    ["Vendor Dependency Review", "Reviewed periodically to ensure operational flexibility and business continuity"],
    ["Last Reviewed", LAST_REVIEWED],
    ["Next Review", NEXT_REVIEW],
    ["Owner", DOCUMENT_OWNER],
  ];
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ownership-portability-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileText;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gold/20 bg-card p-5 mb-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
        <Icon className="h-5 w-5 text-gold" /> {title}
      </h2>
      <div className="mt-3 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 list-disc ps-5">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  );
}

function OwnershipPortabilityPage() {
  return (
    <div className="min-h-dvh bg-gradient-to-b from-background to-muted/30 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>

        <header className="mt-6 mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Platform Ownership & Portability
              </h1>
              <p className="mt-2 text-muted-foreground max-w-3xl">
                Formal compliance and governance documentation outlining Recovery Trauma™
                ownership, technology stack, deployment portability, business continuity
                measures, and vendor dependency review.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Document owner: {DOCUMENT_OWNER} · Last reviewed: {LAST_REVIEWED} · Next review:{" "}
                {NEXT_REVIEW}
              </p>
            </div>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 rounded-lg border border-gold/30 bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-gold/10"
            >
              <FileText className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </header>

        <Section icon={Package} title="Ownership">
          <p className="mb-3">Recovery Trauma™ owns:</p>
          <BulletList items={OWNERSHIP_ITEMS} />
        </Section>

        <Section icon={Layers} title="Technology Stack">
          <p className="mb-3">
            Recovery Trauma™ is built using industry-standard open-source technologies including:
          </p>
          <div className="flex flex-wrap gap-2">
            {TECH_STACK.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </Section>

        <Section icon={Cloud} title="Portability">
          <p className="mb-3">
            The platform is designed to be portable and capable of deployment outside Lovable
            infrastructure.
          </p>
          <p className="mb-2 font-medium text-foreground">Potential deployment environments include:</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {DEPLOYMENT_TARGETS.map((t) => (
              <li
                key={t}
                className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/40 p-2"
              >
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-foreground">{t}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={ShieldCheck} title="Business Continuity">
          <p className="mb-3">
            To reduce platform dependency risk, Recovery Trauma™ maintains:
          </p>
          <BulletList items={CONTINUITY_MEASURES} />
        </Section>

        <Section icon={RefreshCw} title="Vendor Dependency Review">
          <p>
            Vendor dependencies are reviewed periodically to ensure Recovery Trauma™ maintains
            operational flexibility and business continuity.
          </p>
        </Section>

        <Section icon={Database} title="Last Reviewed">
          <p>
            <span className="font-medium text-foreground">{LAST_REVIEWED}</span> · Next scheduled
            review: {NEXT_REVIEW}
          </p>
        </Section>

        <Section icon={FileText} title="Owner">
          <p>
            <span className="font-medium text-foreground">Founder & Product Compliance Lead</span>
            <br />
            Jana Burkhardt
          </p>
        </Section>

        <section className="rounded-xl border border-gold/20 bg-card p-5 mb-8">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <FileText className="h-5 w-5 text-gold" /> Related Governance Documents
          </h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link to="/admin/team-roles" className="text-gold hover:underline">
                Governance & Team Roles
              </Link>{" "}
              — leadership accountability and compliance ownership matrix.
            </li>
            <li>
              <Link to="/admin/escalation-pathway" className="text-gold hover:underline">
                Escalation Pathway
              </Link>{" "}
              — documented decision points for flagged issues and safeguarding concerns.
            </li>
            <li>
              <Link to="/admin/escalation-cases" className="text-gold hover:underline">
                Escalation Cases
              </Link>{" "}
              — live admin workflow for case management and audit trail.
            </li>
          </ul>
        </section>

        <footer className="mt-10 text-xs text-muted-foreground">
          This document forms part of the Recovery Trauma™ Compliance & Governance documentation
          set and is reviewed annually as part of the audit-readiness review cycle.
        </footer>
      </div>
    </div>
  );
}
